<template>
  <div class="chart-container" v-if="isDataReady">
    <div class="custom-legend">
      <div v-for="(color, status) in statusColors" :key="status" class="legend-item">
        <div class="d-flex align-items-center">
          <span class="color-dot" :style="{ backgroundColor: color }"></span>
          <span class="status-name">{{ status }}</span>
        </div>
      </div>
    </div>
    <pie-chart ref="pie" class="chart-wrapper" :chartData="formattedData" :options="chartOptions">
    </pie-chart>
    <div class="device-list-container">
      <h5 class="mb-3">
        Selected Status: {{ selectedStatus || 'None' }}
        <div class="mt-3">
          Devices: <b-badge variant="primary">{{ filteredDevices.length }}</b-badge>
        </div>
      </h5>

      <div v-if="stalledInProgressDevices.length > 0" class="stalled-banner">
        <b-icon icon="exclamation-triangle-fill" class="stalled-banner__icon"></b-icon>
        <span>
          <strong>{{ stalledInProgressDevices.length }} Beds statuses</strong> are not moving for more than an hour. There could be some trouble during the update. To retry OTA job perform below actions :
          <ol class="stalled-banner__steps">
            <li>Go to Add Job</li>
            <li>Under <strong>Devices</strong> tab, select <strong>Yellow Warning</strong> icon.</li>
            <li>Click Stalled In-progress (&gt;1hr)</li>
            <li>Select the Bed, and click "Send Enable Bed Request"</li>
            <li>Now Bed is available for new OTA job</li>
          </ol>
        </span>
       
      </div>

      <b-table sticky-header="400px" striped hover :items="devices" :fields="singleColumnField" :filter="filterText"
        empty-text="No devices match this status" show-empty class="border rounded device-details-sort-table" @row-clicked="setDevice">
        <template #cell(id)="data">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ data.item.id }}</strong>
              <div class="small text-muted">{{ data.item.mac }}</div>
            </div>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>


<script>
import Vue from "vue";
import PieChart from '../../../shared/careai/components/PieChart.vue';
import BedManagerService from "../BedManager.service";
import { OTA_STATUS_COLOR_MAPPER, OTA_TERMINAL_STATUSES, OTA_ACTIVELY_PROCESSING_STATUSES } from "../bed-manager-constants";

Vue.component('pie-chart', PieChart);

export default {
  name: 'TaskMetricsChart',
  components: {
    PieChart
  },
  props: {},
  data() {
    return {
      bedManagerService: BedManagerService,
      filterText: '',
      singleColumnField: [
        { key: 'id', label: 'Device Details', sortable: true }
      ],
      devices: [],
      selectedStatus: '',
      statusColors: OTA_STATUS_COLOR_MAPPER
    }
  },
  computed: {
    isDataReady() {
      const job = this.bedManagerService.selectedJob;
      return job && job.taskStatusMetrics && job.taskStatusMetrics.length > 0;
    },

    formattedData() {
      const metrics = this.bedManagerService.selectedJob.taskStatusMetrics.filter(t => t.taskType === 'OTA') || [];
      const counts = metrics.reduce((acc, item) => {
        const status = this.normalizeStatus(item.taskStatus);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const labels = Object.keys(counts);
      const dataValues = Object.values(counts);

      // Generate colors based on status mapping
      const backgroundColors = labels.map((status, index) => {
        return this.statusColors[status.trim()] || `hsl(${(index * 137.5) % 360}, 70%, 50%)`;
      });

      return {
        labels: labels,
        datasets: [{
          data: dataValues,
          backgroundColor: backgroundColors,
          borderColor: '#ffffff',
          borderWidth: 1
        }]
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: false,
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const chartElement = elements[0];
            const index = chartElement._index;

            const status = this.formattedData.labels[index];

            this.setSelectedStatus(status)
            this.populateDevices();
          }
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const currentValue = dataset.data[tooltipItem.index];
              const label = data.labels[tooltipItem.index];

              // Calculate total on the fly for the percentage
              const total = dataset.data.reduce((sum, val) => sum + val, 0);
              const percentage = ((currentValue / total) * 100).toFixed(1);

              return ` ${label}: (${currentValue} / ${percentage}%)`;
            }
          }
        }
      };
    },

    filteredDevices() {
      if (!this.filterText) return this.devices;
      return this.devices.filter(d => d.status === this.filterText);
    },

    stalledInProgressDevices() {
      const metrics = this.bedManagerService.selectedJob.taskStatusMetrics || [];
      const lastUpdatedMap = this.bedManagerService.deviceLastUpdatedById || {};
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      const excluded = new Set([
        ...OTA_TERMINAL_STATUSES.map(s => s.toLowerCase().replace(/\s+/g, '')),
        ...OTA_ACTIVELY_PROCESSING_STATUSES.map(s => s.toLowerCase().replace(/\s+/g, ''))
      ]);
      return metrics.filter((t) => {
        if (t.taskType !== 'OTA') return false;
        const statusLower = String(t.taskStatus || '').toLowerCase().replace(/\s+/g, '');
        if (excluded.has(statusLower)) return false;
        if (statusLower.includes('userack')) return false;
        const lastUpdated = lastUpdatedMap[t.id];
        if (!lastUpdated) return false;
        return new Date(lastUpdated).getTime() < oneHourAgo;
      });
    },

  },
  watch: {
    'bedManagerService.selectedJob.taskStatusMetrics': {
      handler() {
        if (this.selectedStatus) {
          this.populateDevices();
        }
      },
      immediate: true,
      deep: true
    },
    'bedManagerService.tabIndex': function (val) {
      // When metrics tab becomes active (index 4), ensure chart is rendered/updated.
      if (val === 4 && this.isDataReady) {
        this.$nextTick(() => {
          this.ensureChartRendered();
        });
      }
    }
  },
  methods: {
    ensureChartRendered() {
      try {
        const pie = this.$refs.pie;
        if (!pie) return;

        if (pie && pie.$data && pie.$data._chart) {
          pie.$data._chart.resize();
          pie.$data._chart.update();
        } else if (pie && typeof pie.renderChart === 'function') {
          pie.renderChart(this.formattedData, this.chartOptions);
        }
      } catch (e) {
        // non-critical: log and continue
        // eslint-disable-next-line no-console
        console.warn('TaskMetricsChart: failed to ensure chart rendered', e);
      }
    },
    populateDevices() {
      const filteredDevices = this.bedManagerService.selectedJob.taskStatusMetrics.filter(t => t.taskType === 'OTA' && this.normalizeStatus(t.taskStatus) === this.selectedStatus);
      this.devices = filteredDevices.map(({ id, mac }) => ({ id, mac }));
    },
    normalizeStatus(status) {
      return String(status || '').replace('With Errors', '').trim();
    },
    setSelectedStatus(status) {
      this.selectedStatus = status;
    },
    setDevice(event) {
      // remember current tab so closeDevice can restore it
      this.bedManagerService.previousTabIndex = this.bedManagerService.tabIndex;
      this.bedManagerService.selectedDevice = { ...this.bedManagerService.selectedDevice, id: null, data: [] };
      setTimeout(() => {
        this.bedManagerService.selectedDevice = { ...this.bedManagerService.selectedDevice, id: event.id, data: [] };
        setTimeout(() => this.bedManagerService.tabIndex = 5, 25);
      }
        , 1000)
    },

  },

  mounted() {
    // Ensure deviceLastUpdatedById is populated so the stalled banner can evaluate
    const tenant = this.bedManagerService.tenants && this.bedManagerService.tenants.selected && this.bedManagerService.tenants.selected.value;
    if (tenant) {
      this.bedManagerService.fetchDevicesAndStatus(tenant);
    }
  },

}
</script>

<style scoped>
.chart-container {
  padding-top: 30px !important;
  padding: 15px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  .chart-wrapper {
    position: relative;
    height: 500px;
    width: 60%;
  }

  .device-list-container {
    width: 30%;
    margin-left: 10px;
    height: 500px;

    ::v-deep tbody tr {
      cursor: pointer !important;
    }
  }

  .stalled-banner {
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-left: 4px solid #f9a825;
    border-radius: 4px;
    padding: 10px 12px;
    margin-bottom: 12px;
    font-size: 12px;
    color: #5f4400;

    .stalled-banner__icon {
      color: #f9a825;
      margin-right: 6px;
    }

    .stalled-banner__steps {
      margin: 6px 0 0;
      padding-left: 18px;
    }

    .stalled-banner__link {
      color: #00b5da;
      font-weight: 600;
      text-decoration: underline;
      cursor: pointer;
      align-self: flex-start;
      &:hover { color: #0090af; }
    }
  }

  .custom-legend {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
    padding: 15px;
    background-color: #f9f9f9;
    border-radius: 8px;
    border: 1px solid #eee;
  }

  .legend-item {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #444;
  }

  .color-dot {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    margin-right: 8px;
    flex-shrink: 0;
  }

  .status-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
  }
}

.badge {
  background-color: rgba(0, 181, 218, 0.9) !important;
}

/* sticky-header wraps <table> in a div that carries device-details-sort-table, so .table.b-table is a descendant, not the same element */
.device-list-container ::v-deep .device-details-sort-table .table.b-table > thead > tr > [aria-sort] {
  cursor: pointer;
  background-image: none !important;
}

/* th must stay display:table-cell for column layout, so the flex alignment goes on the label div bootstrap-vue renders instead */
.device-list-container ::v-deep .device-details-sort-table .table.b-table > thead > tr > [aria-sort] > div {
  display: flex;
  align-items: center;
}

/* CSS-only glyph (matches ThemedTable.vue's sort icon family) placed inline right after the label so it stays visible even on a full-width single-column header */
.device-list-container ::v-deep .device-details-sort-table .table.b-table > thead > tr > [aria-sort] > div::after {
  content: '\21F5';
  display: inline-block;
  margin-left: 0.35rem;
  font-size: 0.75em;
  opacity: 0.45;
}

.device-list-container ::v-deep .device-details-sort-table .table.b-table > thead > tr > [aria-sort=ascending] > div::after {
  content: '\2191';
  opacity: 1;
}

.device-list-container ::v-deep .device-details-sort-table .table.b-table > thead > tr > [aria-sort=descending] > div::after {
  content: '\2193';
  opacity: 1;
}
</style>